import GenericContent from './generic-content.json'
import ProgramsContent from './programs-content.json'
import PageContent from './pages-content.json'

export function getGenericContent(contentId: string): string {
	const result = GenericContent.find(
		(json) => json.content_id.toLowerCase() === contentId.toLowerCase()
	)
	return result?.data || ''
}

export function getPageContent(
	contentId: string,
	language = 'en-us',
	fromCms = false
): GenericContent | undefined {
	if (!fromCms && contentId) {
		if (language !== 'en-us') return ''

		const result = PageContent.filter((json) => {
			return json.content_id.toLowerCase() === contentId.toLowerCase()
		})[0]

		if (!result) return undefined
		return typeof result.data == 'string' || Array.isArray(result.data) ? result.data : undefined
	}
	return undefined
}

export interface PageContentItem {
	title: string
	description: string
}

export function getPageContentArry(
	contentId: string,
	language = 'en-us',
	fromCms = false
): PageContentItem[] | undefined {
	if (!fromCms && contentId) {
		if (language !== 'en-us') return

		const result = PageContent.filter((json) => {
			return json.content_id.toLowerCase() === contentId.toLowerCase()
		})[0]

		if (!result) return
		return result?.dataArry
	}
	return undefined
}

// Body can be a string (Vori), string[] (legacy paragraphs, e.g. Onduo), or an HTML sections object (EAP, Blue365)
export interface BodySections {
	section_top: string
	section_middle?: string
	section_bottom?: string
}

export interface ProgramData {
	title: string
	// string (Vori), string[] (legacy paragraphs, e.g. Onduo), or HTML sections object (EAP, Blue365)
	body: BodySections | string | string[]
	image?: string
	templateType?: string
	buttonLabel: string
	buttonLink: string // "" when buttonSSO is used
	buttonSSO: string // e.g. "blue365", or "" when buttonLink is used
}

// Consent/exit modal shown before leaving the site
export interface ProgramExitModal {
	content: string // HTML
	checkboxes: string[] // HTML strings, one per required checkbox
	buttons: {
		cancelLabel: string
		acceptLabel: string
	}
	showPrint?: boolean
	useCookie?: boolean
}

export interface ProgramItemBase {
	content_id: string
	date_expire?: string
	date_logic?: { date: string; content: string }[]
	data: ProgramData
	dataExitModal?: ProgramExitModal // only on some items
}

// Dynamic sibling keys like "data-june202412"
export type DatedDataKeys = {
	[K in `data-${string}`]?: ProgramData
}

export type ProgramItem = ProgramItemBase & DatedDataKeys

export type DatedKey = `data-${string}`

export type ProgramContent = ProgramData | undefined

export function getDateContent(
	dateLogic: NonNullable<ProgramItem['date_logic']>,
	now = new Date()
): DatedKey | undefined {
	// Parse once; date-only strings ("2024-06-01") are treated as LOCAL midnight
	// instead of UTC midnight, so the switch happens at the user's local midnight.
	const parse = (s: string): number => {
		const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
		return dateOnly
			? new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]).getTime()
			: new Date(s).getTime() // full timestamps keep their explicit offset
	}

	const nowMs = now.getTime()

	const match = dateLogic
		.map((d) => ({ ...d, ms: parse(d.date) }))
		.filter((d) => !Number.isNaN(d.ms) && d.ms <= nowMs) // drop invalid dates, keep entries in effect
		.sort((a, b) => b.ms - a.ms)[0] // latest first, numeric so format differences don't matter

	// Only accept keys that follow the data-* convention
	return match?.content.startsWith('data-') ? (match.content as DatedKey) : undefined
}

export function getProgram(contentId: string, language = 'en-us', fromCms = false): ProgramContent {
	if (fromCms || !contentId || language !== 'en-us') return

	const result = (ProgramsContent as ProgramItem[]).find(
		(json) => json.content_id.toLowerCase() === contentId.toLowerCase()
	)
	if (!result) return

	// Dated override if one applies, otherwise the default data block
	const key = result.date_logic ? getDateContent(result.date_logic) : undefined
	return (key && result[key]) || result.data || undefined
}

export const getContentFull = (
	contentId: string,
	language: string = 'en-us',
	fromCms: boolean = false
) => {
	if (!fromCms) {
		if (language !== 'en-us') return
		const result = ProgramsContent.filter(
			(json) => json.content_id.toLowerCase() === contentId.toLowerCase()
		)
		return result.length === 0 ? contentId : result[0]
	}
	return contentId
}

export const getExitModal = (contentId: string): ProgramExitModal | undefined => {
	const result = (ProgramsContent as ProgramItem[]).find(
		(json) => json.content_id?.toLowerCase() === contentId.toLowerCase()
	)
	return result?.dataExitModal
}
